import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {BibliographyItem} from '../types/BibliographyItem';
import {Publication} from '../types/Publication';
import bibliographyItemsService from '../services/bibliographyItemsService';
import publicationsService from '../services/publicationsService';
import {useNavigate, useParams} from 'react-router-dom';
import AdminFormHeader from "../../../layout/admin/AdminFormHeader";
import {AdminButton, AdminForm, AdminFormSection, AdminInputDropdown, AdminInputText} from "../../../layout/admin";
import AdminRichTextArea from "../../../layout/admin/AdminRichTextArea";
import {useFormErrorHandling} from "../../../common/hooks/useFormErrorHandling";

const BibliographyItemForm: React.FC = () => {
  const {t} = useTranslation();
  const [formData, setFormData] = useState({
    abbreviation: '',
    content: '',
    publicationId: ''
  });
  const [bibliographyItem, setBibliographyItem] = useState<BibliographyItem | null>(null);
  const [publications, setPublications] = useState<Publication[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const {id} = useParams<{ id: string }>();
  const isEditMode = Boolean(id);
  const {errors, handleApiError, clearErrors} = useFormErrorHandling();

  useEffect(() => {
    fetchPublications();
    if (isEditMode && id) {
      fetchBibliographyItem(id);
    }
  }, [id, isEditMode]);

  const fetchPublications = async (): Promise<void> => {
    const publicationsData = await publicationsService.getAllPublications();
    setPublications(publicationsData);
  };

  const fetchBibliographyItem = async (bibliographyItemId: string): Promise<void> => {
    setLoading(true);
    try {
      const bibliographyItemData = await bibliographyItemsService.getBibliographyItem(bibliographyItemId);
      setBibliographyItem(bibliographyItemData);
      setFormData({
        abbreviation: bibliographyItemData.abbreviation,
        content: bibliographyItemData.content,
        publicationId: bibliographyItemData.publication?.id || ''
      });
    } catch (error) {
      console.error('Error fetching bibliography item:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string): void => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleContentChange = (html: string): void => {
    setFormData(prev => ({
      ...prev,
      content: html
    }));
  };

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setLoading(true);
    clearErrors();

    try {
      if (isEditMode && bibliographyItem) {
        const updatedBibliographyItem: BibliographyItem = {
          id: bibliographyItem.id,
          abbreviation: formData.abbreviation,
          content: formData.content,
          publication: formData.publicationId
            ? {
              id: formData.publicationId,
              name: publications.find(p => p.id === formData.publicationId)?.name || ''
            }
            : undefined
        };
        await bibliographyItemsService.updateBibliographyItem(updatedBibliographyItem);
      } else {
        await bibliographyItemsService.createBibliographyItem({
          abbreviation: formData.abbreviation,
          content: formData.content,
          publication: formData.publicationId
            ? {
              id: formData.publicationId,
              name: publications.find(p => p.id === formData.publicationId)?.name || ''
            }
            : undefined
        });
      }
      navigate('/bibliography-items');
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };

  const publicationOptions = [
    {label: '-- None --', value: ''},
    ...publications.map(pub => ({
      label: pub.name,
      value: pub.id
    }))
  ];

  return (
    <>
      <AdminFormHeader
        title={bibliographyItem ? t('admin.bibliographyItems.title.edit') : t('admin.bibliographyItems.title.create')}
        subtitle={bibliographyItem ? t('admin.bibliographyItems.subtitle.edit') : t('admin.bibliographyItems.subtitle.create')}
      />

      <AdminForm
        leftColumn={
          <>
            <AdminFormSection title={t('admin.common.sections.basicInfo')}>
              <AdminInputText
                value={formData.abbreviation}
                onChange={(e) => handleInputChange('abbreviation', e.target.value)}
                name="abbreviation"
                id="abbreviation"
                label={t('admin.bibliographyItems.fields.abbreviation')}
                required={true}
                error={errors.abbreviation}
              />

              <AdminRichTextArea
                value={formData.content}
                onChange={handleContentChange}
                name="content"
                id="content"
                label={t('admin.bibliographyItems.fields.content')}
                required={true}
                error={errors.content}
              />

              <AdminInputDropdown
                value={formData.publicationId}
                onChange={(e) => handleInputChange('publicationId', e.target.value)}
                name="publicationId"
                id="publicationId"
                label={t('admin.bibliographyItems.fields.publication')}
                options={publicationOptions}
                error={errors.publication}
              />
            </AdminFormSection>
          </>
        }
        formActions={
          <>
            <AdminButton
              type="button"
              label={t('admin.common.actions.cancel')}
              onClick={() => navigate("/bibliography-items")}
            />
            <AdminButton
              type="submit"
              label={bibliographyItem ? t('admin.common.actions.update') : t('admin.common.actions.create')}
              variant="black"
              disabled={loading}
            />
          </>
        }
        onSubmit={handleSubmit}
      />
    </>
  );
};

export default BibliographyItemForm;


